import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Card, Grid } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getJobById,
  findTeamById,
  IJob,
  ITeam,
  amplitude,
  shareJobWithTeam,
  getMyTeams,
  getAllTeamsInWorkspace,
} from 'wisestep-api';
import {
  FormWizard,
  WsAutoComplete,
  WsButton,
  WsColors,
  WsIconPublish,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { useJobSettingsContext } from '../../../context/jobsListContext';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { extractTeamNameAndId, getAssignedTeams } from '../helper';
import { defaultValues } from '../listing/components/viewJd/editJobAdminTeam/EditJobAdminTeam';
import JobPageParams from '../types/JobPageParams';

import AssignCard from './components/AssignCard';
import { JobDetailsCard } from './components/jobDetailsCard';
import { IAssignState } from './types';

const Assign = () => {
  const navigate = useNavigate();
  const workspace = useWorkspaceContext();
  const navigateToPostJob = () => navigate('/jobs/new');
  const params = useParams<JobPageParams>();
  const jobId = params.jobId ?? '';
  const methods = useForm();
  const jobFieldSettings = useJobSettingsContext();

  const [teamsResponse, setTeamsResponse] = useState<IAssignState>();

  const [teamsNames, setTeamsNames] = useState<string[]>([]);

  const [disable, setDisable] = useState<boolean>(true);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const isVancyWorkspace = workspace?.id == 'a61ed280-61fa-4e19-8e54-88bffcc24a75' ?? false;

  const isTeamSelectionMandatory = jobFieldSettings?.adminTeamSelectionMandatory ?? false;

  useEffect(() => {
    const teams = async () => {
      try {
        const assignState: IAssignState = {};
        const teamsOwn = await getMyTeams();
        const teamBelongsto = await getAllTeamsInWorkspace();
        const jobDetailsResponse = await getJobById(jobId);

        assignState.jobDetailsResponse = jobDetailsResponse.data;
        assignState.currentUserTeams = [...(teamsOwn?.data ?? []), ...(teamBelongsto?.data ?? [])];

        !isEmpty(assignState.currentUserTeams) &&
          setTeamsNames(getAssignedTeams(extractTeamNameAndId(assignState.currentUserTeams)));

        setTeamsResponse(assignState);

        setDisable(isTeamSelectionMandatory && isEmpty(assignState.jobDetailsResponse?.team));
      } catch (e) {
        const error = e as Error | AxiosError;
        if (!axios.isAxiosError(error)) {
          console.log(error);
        }
        console.log(error);
      }
    };

    teams();
  }, [isTeamSelectionMandatory, jobId]);

  const navigateToPublish = () => {
    if (!disable) navigate(`/jobs/${jobId}/publish`);
    else setNotification(failed('Please select admin team to proceed further'));
  };

  const menuItems = [
    {
      id: 0,
      label: 'Complete Job details',
    },
    {
      id: 1,
      label: 'Assign Team',
    },
    {
      id: 2,
      label: 'Publish Job',
    },
  ];

  const fetchTeamAndAssign = async (selectedAdminTeams: string[]) => {
    isTeamSelectionMandatory && setDisable(true);
    if (selectedAdminTeams.length > 0) {
      const selectedTeam = teamsResponse?.currentUserTeams?.filter((team) => {
        return team.name == selectedAdminTeams[0];
      });
      methods.setValue('adminTeams', selectedTeam);
      if (selectedTeam && selectedTeam[0]?.id) {
        const AdminTeam = await findTeamById(selectedTeam[0]?.id);
        const selectedAdminTeam: ITeam = AdminTeam.data;
        if (teamsResponse?.jobDetailsResponse) {
          teamsResponse.jobDetailsResponse.team = selectedAdminTeam;
          const job: IJob = teamsResponse.jobDetailsResponse;
          defaultValues.mappingCount = job.mappings ?? 0;
          defaultValues.submissionCount = job.submissions ?? 0;
          defaultValues.jobId = jobId ?? '';
          defaultValues.team = selectedAdminTeam;

          try {
            await shareJobWithTeam(defaultValues);
            amplitude('Job_Adminteam_Selected', {
              clickedOn: 'Select Admin Team',
              pageName: 'jobs assign',
              pageUrl: window.location.href,
              workspaceName: workspace.name,
              planType: workspace.planType,
              interfaceType: 'Web',
            });
            setNotification(success(`Job has been shared to ${job.team?.name} successfully `));
            setDisable(false);
          } catch {
            setNotification(failed('Failed to share job as Admin team, Please try again !! '));
          }
        }
      }
    } else {
      setNotification(warning('Please re-select job Admin team'));
    }
  };

  const handleOnValueEnter = async (value: string) => {
    if (!isEmpty(value)) {
      const filteredTeams = teamsResponse?.currentUserTeams?.filter((team) =>
        team.name?.toLowerCase().includes(value.toLowerCase())
      );
      filteredTeams && setTeamsNames(getAssignedTeams(filteredTeams));
    } else {
      setTeamsNames(getAssignedTeams(teamsResponse?.currentUserTeams ?? []));
    }
  };

  return (
    <FormWizard
      menuItems={menuItems}
      currentStep={1}
      title="Post a new Job"
      onArrowButtonClick={navigateToPostJob}
    >
      {teamsResponse?.jobDetailsResponse && jobId && (
        <>
          <JobDetailsCard jobsData={teamsResponse?.jobDetailsResponse} />
          <Grid container px={3}>
            <Grid item xs={12} p={3} alignContent={'center'}>
              <Card variant="outlined">
                <Card sx={{ bgcolor: WsColors.TertiaryShades50, p: 3, alignContent: 'center' }}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>Assign Admin Team</WsTypography>
                </Card>
                <Card sx={{ p: 4 }}>
                  <FormProvider {...methods}>
                    <WsAutoComplete
                      onValueEnter={handleOnValueEnter}
                      onTagsChange={fetchTeamAndAssign}
                      placeholder="Type to Select Admin Team"
                      initialValue={[teamsResponse?.jobDetailsResponse?.team?.name ?? '']}
                      optionsInStringArray={teamsNames}
                      multiple={false}
                      label="Select Admin Team"
                      name="adminTeams"
                      showMandatorySymbol={isTeamSelectionMandatory}
                    />
                  </FormProvider>
                </Card>
              </Card>
            </Grid>
            <Grid p={3}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE}>Assign Job to</WsTypography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              m={2}
              spacing={2}
              justifyContent={`${isVancyWorkspace}` ? 'space-evenly' : 'space-between'}
            >
              {!isVancyWorkspace && (
                <Grid item xs={4}>
                  <AssignCard
                    title="Recruiters"
                    name="recruiters"
                    icon={<GroupOutlinedIcon fontSize="large" color="action" />}
                    jobId={jobId}
                    disableButton={disable}
                  />
                </Grid>
              )}

              <Grid item xs={4}>
                <AssignCard
                  name="teams"
                  title="Teams"
                  icon={<GroupsOutlinedIcon fontSize="large" color="action" />}
                  jobId={jobId}
                  disableButton={disable}
                />
              </Grid>
              <Grid item xs={4}>
                <AssignCard
                  jobId={jobId}
                  name="partners"
                  title="Partners"
                  icon={<ContactPageOutlinedIcon fontSize="large" color="action" />}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      <Grid container justifyContent={'flex-end'}>
        <WsButton
          variant="outlined"
          startIcon={<WsIconPublish />}
          size="large"
          onClick={navigateToPublish}
          disabled={disable}
        >
          Publish
        </WsButton>
      </Grid>
    </FormWizard>
  );
};

export default Assign;
